import { BACKEND_URL } from "@/lib/backendUrl";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, ShieldCheck } from "lucide-react";

const API_BASE = `${BACKEND_URL}/api`;
const words = (s) => String(s || "").replace(/_/g, " ");

const Block = ({ title, children, testId }) => (
  <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur"
           data-testid={testId}>
    <h2 className="text-lg font-medium text-white">{title}</h2>
    <div className="mt-3 space-y-1.5 text-[12.5px] text-neutral-400">{children}</div>
  </section>
);

export default function PublicAnnualReport() {
  const { reportRef } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API_BASE}/public/annual-report/${reportRef}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("No published annual report"))))
      .then(setData)
      .catch((e) => setError(e.message));
  }, [reportRef]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b0f14] px-6">
        <p className="text-[13px] text-neutral-400" data-testid="public-annual-error">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b0f14]">
        <p className="text-[13px] text-neutral-500">Opening the report…</p>
      </div>
    );
  }

  const c = data.counters;
  return (
    <div className="min-h-screen bg-[#0b0f14] px-6 py-16 text-neutral-200">
      <div className="mx-auto max-w-3xl space-y-8">
        <header className="space-y-3">
          <p className="text-[11px] uppercase tracking-[0.22em] text-emerald-400/80">
            {data.charity} · annual governance report · issued {data.issued_at}
          </p>
          <h1 className="text-4xl font-semibold leading-tight text-white sm:text-5xl"
              data-testid="public-annual-title">
            {data.title}
          </h1>
          <p className="text-base text-neutral-400">
            {data.date_from} to {data.date_to} · version {data.version} · {words(data.status)}
            {data.registers.length > 0 && ` · ${data.registers.join(" · ")}`}
          </p>
          <a href={`${API_BASE}/public/annual-report/${reportRef}/pdf`} target="_blank"
             rel="noreferrer" data-testid="public-annual-pdf"
             className="inline-flex items-center gap-1.5 text-[12.5px] text-emerald-300 hover:underline">
            <Download className="h-3.5 w-3.5" /> Download the signed report
          </a>
        </header>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {[["Meetings held", `${c.meetings_held}/${c.meetings}`],
            ["Resolutions carried", `${c.resolutions_carried}/${c.resolutions}`],
            ["Elections declared", c.elections],
            ["Average turnout", `${c.average_turnout}%`],
            ["Interests declared", c.conflicts],
            ["Rulebook versions", c.rule_changes]].map(([label, value]) => (
            <div key={label} className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
              <p className="text-[10.5px] uppercase tracking-wider text-neutral-500">{label}</p>
              <p className="mt-0.5 font-mono text-lg text-white">{value}</p>
            </div>
          ))}
        </div>

        <Block title="The trustees' narrative" testId="public-annual-narrative">
          <p className="leading-relaxed">{data.narrative.summary}</p>
          <p className="leading-relaxed">{data.narrative.achievements}</p>
          <p className="leading-relaxed">{data.narrative.attention}</p>
        </Block>

        <Block title="Meetings" testId="public-annual-meetings">
          {data.meetings.map((m) => (
            <p key={m.meeting_ref}>
              {m.starts_at.slice(0, 10)} · {m.title} ({m.type_label}) · {m.attended} attended
              {m.quorum_met === false ? " · quorum not met" : ""}
            </p>
          ))}
        </Block>

        <Block title="Resolutions" testId="public-annual-resolutions">
          {data.resolutions.length === 0 && <p>No resolution was decided in the year.</p>}
          {data.resolutions.map((r) => (
            <p key={r.item_ref}>
              {r.resolution_ref || r.item_ref} · {r.title} —{" "}
              <span className="text-white">{words(r.status)}</span> ({r.for} for, {r.against}{" "}
              against, {r.abstain} abstained of {r.eligible})
            </p>
          ))}
        </Block>

        <Block title="Elections and who held office" testId="public-annual-elections">
          {data.elections.map((e) => (
            <p key={e.election_ref}>
              {e.name} · turnout {e.turnout?.percent}% · {e.posts.map(
                (p) => `${p.title}: ${p.elected.join(", ") || "no result"}`).join(" · ")}
            </p>
          ))}
          {data.office_holders.map((h) => (
            <p key={`${h.post_title}-${h.name}-${h.term_start}`}>
              {h.post_title} — <span className="text-white">{h.name}</span> · {h.term_start} to{" "}
              {h.term_end} · {h.route}
            </p>
          ))}
        </Block>

        <footer className="space-y-2 border-t border-white/10 pt-6 text-[12px] text-neutral-500">
          {data.signatures.map((s) => (
            <p key={s.name} data-testid="public-annual-signature">
              <ShieldCheck className="mr-1.5 inline h-3.5 w-3.5 text-emerald-400" />
              Signed by {s.name}, {s.office}, on {s.signed_at}.
            </p>
          ))}
          {data.adoption?.resolution_ref && (
            <p>
              Adopted by the board as {data.adoption.resolution_ref} —{" "}
              {words(data.adoption.outcome?.status)}.
            </p>
          )}
          {data.superseded_by && (
            <p>A corrected version has since been issued as {data.superseded_by}.</p>
          )}
        </footer>
      </div>
    </div>
  );
}
